/* eslint-disable no-unused-vars */
/* eslint-disable react-hooks/exhaustive-deps */
import { useCallback, useMemo, useState } from "react";
import { fixEquipmentFields } from "../fields/fixEquipmentFields";
import SimpleBody from "../../../../components/forms/SimpleBody";
import useNotification from "../../../../hooks/useNotification";
import useBaseService from "../../../../hooks/services/useBaseService";
import { useNavigate } from "react-router-dom";
import {
  convertStringsToFloats,
  getMissingRequiredFields,
  normalizeFormErrors,
  setDefaultFieldValues,
} from "../../../../utils/objectUtils";
import useForm from "../../../../hooks/useForm";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { transformFiltersForUrl } from "../../../../utils/transformFiltersForUrl";
import { EquipmentEnum } from "../../../../enums/equipment-enum";
import { entities } from "../../../../constants/entities";
import { v4 as uuidv4 } from "uuid";
import useLoader from "../../../../hooks/useLoader";
import useRemoteSelectOptions from "../../../../hooks/useRemoteSelectOptions";

/**
 * * Lógica para formulário de atrelar equipamentos
 */
export function useAttachEquipment({ formData, setFormData }) {
  const navigate = useNavigate();
  const { showNotification } = useNotification();
  const { showLoader, hideLoader } = useLoader();
  const queryClient = useQueryClient();

  const {
    get: baseServiceGet,
    post: baseServicePost,
    formErrors,
    setFormErrors,
  } = useBaseService(navigate);
  const [_, attachFixEquipmentSection] = fixEquipmentFields;

  const {
    formData: attachFormData,
    handleChange: handleAttachChange,
    setFormData: setAttachFormData,
  } = useForm(setDefaultFieldValues([attachFixEquipmentSection]), [
    attachFixEquipmentSection,
  ]);

  const [equipments, setEquipments] = useState([]);

  const handleEquipmentsChange = useCallback(
    (newEquipments) => {
      setEquipments(newEquipments);
      setFormData((prev) => ({ ...prev, attached_equipment: newEquipments }));
    },
    [setFormData],
  );

  const blockedEquipmentIds = useMemo(() => {
    const ids = [];

    // equipamentos já atrelados
    if (equipments.length > 0) {
      equipments.forEach((item) => {
        if (item.equipment_id?.value) {
          ids.push(item.equipment_id.value);
        }
      });
    }

    return ids;
  }, [equipments]);

  const [actionEquipment, setActionEquipment] = useState("");

  const equipmentOptions = useRemoteSelectOptions({
    queryKey: ["attached_equipments"],
    fetcher: async ({ page = 1, perPage = 15, search = "" }) => {
      const filters = transformFiltersForUrl({
        deleted_at: false,
        status_id: EquipmentEnum.AVAILABLE,
        is_completed: true,
        ...(search && { number: search, name: search }),
      });
      const params = { filters, page, perPage };

      const response = await baseServiceGet(entities.equipments.get, params);
      return response.result;
    },
    fieldId: "attached_equipment.equipment_id",
    isMulti: false,
    mapToOption: (equipment) => ({
      value: equipment.id,
      label: `${equipment.number} - ${equipment.name}`,
      raw: equipment,
    }),
    formData,
    setFormData,
    perPage: 15,
    enablePagination: true,
    action: actionEquipment,
    dependencies: equipments,
  });

  const attachedEquipmentSelects = {
    "attached_equipment.equipment_id": equipmentOptions,
  };
  const getAttachedOptions = useCallback(
    (fieldId) => attachedEquipmentSelects[fieldId]?.options || [],
    [attachedEquipmentSelects],
  );
  const getAttachedSelectedValue = useCallback(
    (fieldId) => attachedEquipmentSelects[fieldId]?.selectedValue || null,
    [attachedEquipmentSelects],
  );

  const getSelectMeta = useCallback(
    (fieldId) => {
      const selectConfig = attachedEquipmentSelects[fieldId];
      if (!selectConfig) return {};

      return {
        hasNextPage: selectConfig.hasNextPage,
        isFetchingNextPage: selectConfig.isFetchingNextPage,
        isLoading: selectConfig.isLoading,
        isFetching: selectConfig.isFetching,
        handleMenuScrollToBottom: selectConfig.handleMenuScrollToBottom,
        handleSearch: selectConfig.handleSearch,
        onFocus: selectConfig.activateQuery,
      };
    },
    [attachedEquipmentSelects],
  );

  const handleSaveEquipment = useCallback(
    (equipmentToEdit) => {
      let updatedEquipments;
      const equipmentData = convertStringsToFloats(
        attachFormData.attached_equipment,
      );

      // Validar campos obrigatórios
      const missingFields = getMissingRequiredFields({
        itemData: equipmentData,
        fields: attachFixEquipmentSection.fields,
        prefix: "attached_equipment",
        ignore: [],
      });

      if (missingFields.length > 0) {
        showNotification(
          "warning",
          "Preencha todos os campos obrigatórios para equipamento",
        );
        return false;
      }

      if (equipmentToEdit) {
        updatedEquipments = equipments.map((equipment) =>
          equipment.id === equipmentToEdit.id
            ? { ...equipment, ...equipmentData }
            : equipment,
        );
      } else {
        updatedEquipments = [
          ...equipments,
          { id: uuidv4().slice(0, 8), ...equipmentData },
        ];
      }

      setEquipments(updatedEquipments);
      handleEquipmentsChange(updatedEquipments);
      return true;
    },
    [equipments, attachFormData, handleEquipmentsChange],
  );

  const initializeEquipmentForm = useCallback(
    (equipmentData) => {
      setAttachFormData({ attached_equipment: equipmentData || {} });
    },
    [attachFormData],
  );

  const renderEquipmentFormBody = useCallback(
    ({ fields, formData, handleFieldChange, formErrors }) => {
      return (
        <SimpleBody
          fields={fields}
          formErrors={normalizeFormErrors(formErrors)}
          formData={formData}
          handleFieldChange={handleFieldChange}
          getOptions={getAttachedOptions}
          getSelectedValue={getAttachedSelectedValue}
          getSelectMeta={getSelectMeta}
        />
      );
    },
    [formErrors, getAttachedOptions, getAttachedSelectedValue],
  );

  const getEquipmentDisplayValue = useCallback((item, fieldId) => {
    const fieldName = fieldId.split(".").pop();
    const value = item[fieldName];

    if (value && typeof value === "object" && "label" in value) {
      return value.label;
    }

    return value || "";
  }, []);

  const createAttachment = useMutation({
    mutationFn: async ({ equipmentId, payload }) => {
      const response = await baseServicePost(
        entities.equipments.attach.create(equipmentId),
        payload,
      );
      return response;
    },
    onMutate: () => {
      showLoader();
    },
    onSuccess: (response) => {
      hideLoader();
      if (response?.status === 200) {
        queryClient.invalidateQueries(["equipments"]);
        showNotification("Equipamento atrelado com sucesso!");
        navigate("/aluguel/equipamentos/");
      }
    },
    onError: (error) => {
      if (error.response?.data?.errors) {
        setFormErrors(error.response.data.errors);
      }
      hideLoader();
    },
  });

  return {
    // * Formulário
    attachFormData,
    handleAttachChange,
    setAttachFormData,

    // * Lógica da tabela de equipamentos
    equipments,
    handleEquipmentsChange,
    handleSaveEquipment,
    initializeEquipmentForm,
    renderEquipmentFormBody,
    getEquipmentDisplayValue,

    // * Mutations
    createAttachment,

    // * Actions
    setActionEquipment,
  };
}
